import type { SxProps, Theme } from '@mui/material/styles';

const IMG_SIZE_CLASS = /^img-(small|medium|large|full)$/;
const IMG_ALIGN_CLASS = /^img-(left|center|right)$/;
const IMG_STYLE_CLASS = /^img-(rounded|circle|shadow|border|thumbnail)$/;

/** Floated images on small screens leave room for wrapped text. */
export const FRONTEND_FLOAT_IMAGE_MOBILE_MAX = '50%';

const FLOAT_SIZE_MOBILE_SELECTORS = [
  '.img-left',
  '.img-right',
  '.img-left.img-small',
  '.img-right.img-small',
  '.img-left.img-medium',
  '.img-right.img-medium',
  '.img-left.img-large',
  '.img-right.img-large',
  '.img-left.img-responsive',
  '.img-right.img-responsive',
].join(', ');

function applyFrontendImageSizeSx(
  sx: Record<string, unknown>,
  sizeToken: string | undefined,
  isFloated: boolean,
) {
  const mobileMax = isFloated ? FRONTEND_FLOAT_IMAGE_MOBILE_MAX : '100%';

  if (sizeToken === 'img-small') {
    sx.maxWidth = { xs: mobileMax, sm: 'min(200px, 100%)' };
  } else if (sizeToken === 'img-medium') {
    sx.maxWidth = { xs: mobileMax, sm: 'min(400px, 100%)' };
  } else if (sizeToken === 'img-large') {
    sx.maxWidth = { xs: mobileMax, sm: 'min(600px, 100%)' };
  } else if (sizeToken === 'img-full') {
    sx.width = '100%';
    sx.maxWidth = '100%';
  } else {
    sx.maxWidth = isFloated ? { xs: mobileMax, sm: '100%' } : '100%';
    if (!isFloated) {
      sx.display = 'block';
    }
  }
}

/** Mobile max-width for floated rich-text images (GlobalStyles fallback). */
export function frontendFloatedImageMobileGlobalStyles(theme: Theme) {
  return {
    [theme.breakpoints.down('sm')]: {
      [FLOAT_SIZE_MOBILE_SELECTORS]: {
        maxWidth: FRONTEND_FLOAT_IMAGE_MOBILE_MAX,
      },
    },
  };
}

const IMG_SIZE_NO_RESPONSIVE = ':not(.img-small):not(.img-medium):not(.img-large):not(.img-full)';

/** Plain CSS for TinyMCE content_style (applies to editor body). */
export const tinymceImageContentCss = `
  img.img-left { float: left; margin: 0 1rem 1rem 0; }
  img.img-right { float: right; margin: 0 0 1rem 1rem; }
  img.img-center { display: block; margin: 0 auto 1rem auto; }
  img.img-full { width: 100%; height: auto; }
  img.img-small { max-width: 200px; height: auto; }
  img.img-medium { max-width: 400px; height: auto; }
  img.img-large { max-width: 600px; height: auto; }
  img.img-rounded { border-radius: 8px; }
  img.img-circle { border-radius: 50%; }
  img.img-shadow { box-shadow: 0 4px 8px rgba(0,0,0,0.1); }
  img.img-border { border: 2px solid #ddd; }
  img.img-thumbnail { padding: 4px; background-color: #fff; border: 1px solid #ddd; border-radius: 4px; }
  img.img-responsive${IMG_SIZE_NO_RESPONSIVE} { max-width: 100%; height: auto; display: block; }
  img${IMG_SIZE_NO_RESPONSIVE} { max-width: 100%; height: auto; }
`;

/** Same rules scoped to .mce-content-body for inline editor injected styles. */
export const tinymceImageEditorCss = tinymceImageContentCss
  .split('\n')
  .filter(line => line.trim())
  .map(line => `  .mce-content-body ${line.trim()}`)
  .join('\n');

/** Resolve TinyMCE image class tokens to MUI sx for public-page rendering. */
export function parseImageVisualFromClass(className: string): SxProps<Theme> {
  const tokens = className.split(/\s+/).filter(Boolean);
  const sizeToken = tokens.find(token => IMG_SIZE_CLASS.test(token));
  const alignToken = tokens.find(token => IMG_ALIGN_CLASS.test(token));
  const styleTokens = tokens.filter(token => IMG_STYLE_CLASS.test(token));

  return (theme: Theme) => {
    const isFloated = alignToken === 'img-left' || alignToken === 'img-right';
    const sx: Record<string, unknown> = {
      width: 'auto',
      height: 'auto',
      objectFit: 'contain',
      boxSizing: 'border-box',
    };

    applyFrontendImageSizeSx(sx, sizeToken, isFloated);

    if (alignToken === 'img-left') {
      sx.float = 'left';
      sx.margin = '0 1rem 1rem 0';
    } else if (alignToken === 'img-right') {
      sx.float = 'right';
      sx.margin = '0 0 1rem 1rem';
    } else if (alignToken === 'img-center') {
      sx.display = 'block';
      sx.margin = '0 auto 1rem auto';
    }

    for (const token of styleTokens) {
      switch (token) {
        case 'img-rounded':
          sx.borderRadius = 8;
          break;
        case 'img-circle':
          sx.borderRadius = '50%';
          break;
        case 'img-shadow':
          sx.boxShadow = theme.shadows[2];
          break;
        case 'img-border':
          sx.border = `2px solid ${theme.palette.divider}`;
          break;
        case 'img-thumbnail':
          sx.padding = 4;
          sx.backgroundColor = theme.palette.background.paper;
          sx.border = `1px solid ${theme.palette.divider}`;
          sx.borderRadius = theme.shape.borderRadius;
          break;
        default:
          break;
      }
    }

    return sx;
  };
}
